import React from 'react';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Shield } from 'lucide-react';

interface VenueHeaderProps {
  name: string;
  address: string;
  distance?: number;
  logo?: string;
  onClick?: () => void;
  onCopyAddress?: () => void;
}

export const VenueHeader: React.FC<VenueHeaderProps> = ({
  name,
  address,
  distance,
  logo,
  onClick,
  onCopyAddress
}) => {
  return (
    <div className="mb-3">
      <div 
        className="flex items-center gap-3 mb-2 flex-wrap"
      >
        {logo && (
          <button className={cn(
            "flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden border cursor-pointer relative",
            "border-dark-400 hover:border-gold-400 hover:scale-105",
            "transition-all duration-200"
          )}
          onClick={onClick}>
            <img
              src={logo}
              alt={`${name} logo`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Replace with Shield icon when image fails to load
                const target = e.currentTarget;
                const parent = target.parentElement;
                if (parent) {
                  // Create shield container
                  const container = document.createElement('div');
                  container.className = "w-full h-full flex items-center justify-center bg-dark-300";
                  
                  // Create SVG shield icon
                  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                  svg.setAttribute("viewBox", "0 0 24 24");
                  svg.setAttribute("width", "16");
                  svg.setAttribute("height", "16");
                  svg.setAttribute("fill", "none");
                  svg.setAttribute("stroke", "currentColor");
                  svg.setAttribute("stroke-width", "2");
                  svg.setAttribute("stroke-linecap", "round");
                  svg.setAttribute("stroke-linejoin", "round");
                  svg.setAttribute("class", "text-gold-400");
                  
                  // Create shield path
                  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                  path.setAttribute("d", "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z");
                  
                  svg.appendChild(path);
                  container.appendChild(svg);
                  
                  // Replace the img with the container
                  parent.replaceChild(container, target);
                }
              }}
            />
          </button>
        )}
        
        {!logo && (
          <button 
            className={cn(
              "flex-shrink-0 w-8 h-8 rounded-lg border cursor-pointer",
              "border-dark-400 hover:border-gold-400 hover:scale-105",
              "transition-all duration-200 bg-dark-300 flex items-center justify-center"
            )}
            onClick={onClick}
          >
            <Shield className="h-4 w-4 text-gold-400" />
          </button>
        )}
        <button className={cn(
          "text-lg font-medium text-gold-400 cursor-pointer flex items-center",
          "hover:text-gold-500 hover:scale-[1.02]",
          "transition-all duration-200"
        )}
        onClick={onClick}>
          {name}
          <span className="ml-2 inline-flex items-center text-gold-400 group-hover:translate-x-1 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </span>
        </button>
      </div>

      {/* Address and Distance Row */}
      <div className="flex items-center justify-between cursor-default">
        <div className="flex items-center text-xs text-dark-800 min-w-0">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0 mr-1" />
          <span className="truncate">{address}</span>
        </div>

        {distance !== undefined && (
          <div className="text-xs text-dark-800 ml-3">
            {distance}km away
          </div>
        )}
      </div>
    </div>
  );
};